// Date-range presets for the Team dashboard KPI cards (on-time
// shipment rate, time to ship) and, since 2026-09-17, the Waves /
// Summary history filter. Ranges are inclusive local-calendar dates as
// YYYY-MM-DD — the warehouse runs on UK time and so do the browsers
// viewing it. The working week is Monday → Saturday (per Alex): "this
// week" (the Team default) is this week's Monday → Saturday in full —
// days still ahead show as empty stubs; "last week" ends on Saturday;
// "week to date" runs from this week's Monday to today. "today" (the
// Waves default) and "yesterday" are single days.
export type KpiPreset = "today" | "yesterday" | "this_week" | "wtd" | "last_week" | "mtd" | "custom";
export interface KpiRange { from: string; to: string; }

const iso = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const shift = (d: Date, days: number) => {
    const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    out.setDate(out.getDate() + days);
    return out;
};

// Monday of the ISO week containing `d` (Sunday belongs to the week
// that started the previous Monday).
const mondayOf = (d: Date) => {
    const dow = d.getDay(); // 0 = Sun
    return shift(d, dow === 0 ? -6 : 1 - dow);
};

export const todayIso = (now: Date = new Date()) => iso(now);

// The last `days` calendar days, today included ("last 7 days" = today
// and the six before it) — the Orders Shipped by date tile's default.
export const lastDaysRange = (days: number, now: Date = new Date()): KpiRange => {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return { from: iso(shift(today, -(days - 1))), to: iso(today) };
};

export const presetRange = (preset: KpiPreset, now: Date = new Date()): KpiRange => {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (preset) {
        case "today":
            return { from: iso(today), to: iso(today) };
        case "yesterday": {
            const y = shift(today, -1);
            return { from: iso(y), to: iso(y) };
        }
        case "this_week": {
            const monday = mondayOf(today);
            return { from: iso(monday), to: iso(shift(monday, 5)) };
        }
        case "last_week": {
            const monday = mondayOf(today);
            return { from: iso(shift(monday, -7)), to: iso(shift(monday, -2)) };
        }
        case "mtd":
            return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
        case "wtd":
        case "custom":
        default:
            return { from: iso(mondayOf(today)), to: iso(today) };
    }
};

// The activity endpoints return every completed row of the window, so
// the BE caps it at 31 days. Editing one end of a custom range drags
// the other end along rather than letting the request fail.
export const MAX_ACTIVITY_RANGE_DAYS = 31;

const parseIso = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
};

export const clampSpan = (range: KpiRange, edited: "from" | "to", maxDays = MAX_ACTIVITY_RANGE_DAYS): KpiRange => {
    const from = parseIso(range.from);
    const to = parseIso(range.to);
    const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
    if (days <= maxDays) return range;
    return edited === "from"
        ? { from: range.from, to: iso(shift(from, maxDays - 1)) }
        : { from: iso(shift(to, -(maxDays - 1))), to: range.to };
};
